import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { legendItemsConfig } from 'src/features/forecast/configs/legendItemsConfig';
import { cn } from 'src/lib/utils';

type ForecastLegendProps = {
  className: string;
};

export function ForecastLegend({ className }: ForecastLegendProps) {
  const { t } = useTranslation('forecast');

  return (
    <div className={cn('absolute z-10', className)}>
      <div className='flex flex-row flex-wrap justify-between'>
        {legendItemsConfig.map((item, index) => (
          <div
            className='flex items-center space-x-3 rounded-md p-3'
            key={index}
          >
            <div className='shrink-0'>
              {item.type === 'line' && (
                <div
                  className='h-1 w-6 rounded-full'
                  style={{ backgroundColor: item.color }}
                />
              )}
              {item.type === 'dashed-line' && (
                <div
                  className='h-1 w-6 rounded-full'
                  style={{
                    background: `repeating-linear-gradient(to right, ${item.color} 0, ${item.color} 4px, transparent 4px, transparent 8px)`,
                  }}
                />
              )}
              {item.type === 'dotted-line' && (
                <div
                  className='h-1 w-6 rounded-full'
                  style={{
                    background: `repeating-linear-gradient(to right, ${item.color} 0, ${item.color} 2px, transparent 2px, transparent 4px)`,
                  }}
                />
              )}
              {item.type === 'area' && (
                <div className='relative h-4 w-6'>
                  <div
                    className='absolute inset-0 rounded-xs opacity-20'
                    style={{ backgroundColor: item.color }}
                  />
                  <div
                    className='absolute bottom-0 h-1 w-full rounded-xs'
                    style={{ backgroundColor: item.color }}
                  />
                </div>
              )}
            </div>
            <div className='min-w-0 flex-1'>
              <Tooltip>
                <TooltipTrigger className='flex flex-row items-center outline-hidden'>
                  <p className='text-nowrap font-light text-layout-foreground-secondary text-xs'>
                    {item.label}
                  </p>
                  <Info
                    className='ml-1 h-3 w-3 text-layout-foreground-secondary'
                    strokeWidth={2}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className='font-light text-xs'>{t(item.description)}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
